import { Box, Button, Center, Flex, Grid, Text } from "@chakra-ui/react";
import { Attendance } from "components/atendimentos-components/atendimento-form/types";
import { DropdownField } from "components/dropdown-field";
import { Toast } from "components/toast";
import { useApplicationContext } from "contexts/ApplicationContext";
import { useAttendanceContext } from "contexts/attendance-context";
import { useFormFields } from "hooks/use-form-fields";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { getTabelaOptions } from "../../simulacoes/utils";
import apiDigitacao from "api/api-digitacao";
import AttendanceInput from "components/atendimentos-components/pagina-atendimento/attendance-field/attendance-input";
import { WithInvalidBox } from "components/invalid-box";
import { InputField } from "components/input-field";
import { currencyMaskFormat } from "utils/string-formats";
import {
  getEmprestimoConsignadoCLTColumns as getEmprestimoConsignadoCLTParcelasColumns,
  setParcelasCLTConsig,
  validateSimulacaoConsignadoCLT
} from "./utils";
import { CustomTableColor } from "components/dynamic-table/color-table";
import { openModalDigitacaoConsigCLT } from "./modal-digitacao-consig-clt";
import { requestLoop } from "components/atendimentos-components/atendimento-form/functions/consulta";
import { AxiosResponse } from "axios";

export interface EmprestimoConsignadoCLTProps {
  banco: string;
  cpf: string;
  codeTable: string;
  numberInstallments: number;
  optionValue?: number; // Dropodwn ("Valor liberado", value: 1),  ("Valor Parcela", value: 2)
  valueInstallment?: number; // Caso optionValue == 2 required, se nao for 2 não mandar campo
  value?: number; // Caso optionValue == 1 required, se nao for 1 não mandar campo;
  observacao?: string;
  simulatedCodeTable?: string;
}

export interface POSTResponseSimulacaoCLT {
  banco: "FACTA" | "TWO_S_CONSIG";
  codTable: string;
  status: "PENDENT" | "FINISHED" | "SYSTEM_ERROR";
  prazo: number;
  simulacao: EmprestimoConsignadoCLTProps;
  observacao?: string;
  parcelas: ConsigCLTParcelaProps[];
  valorSalario?: number;
}

export interface ConsigCLTParcelaProps {
  checked: boolean;
  id: number;
  prazo: number;
  codigoTabela?: string;
  taxa: number;
  valorLiberado: number;
  valorParcela: number;
  valorSeguro: number;
  nomeTabela: string;
}

export interface GETResponseConsultaSimulacaoCLT {
  valorLiberado?: number;
  valorParcela?: number;
  banco: string;
  codTable: string;
  observacao?: string;
  parcelas: ConsigCLTParcelaProps[];
  status: POSTResponseSimulacaoCLT["status"];
  codeRequest?: string;
  simulacao: EmprestimoConsignadoCLTProps;
  prazo: number;
}

export function EmprestimoConsignadoCLT({
  telaAtendimento
}: {
  telaAtendimento: boolean;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSimulacaoOnDigitacao, setIsLoadingSimulacaoOnDigitacao] =
    useState(false);
  const [isLoadingTabela, setIsLoadingTabela] = useState(false);
  const [tabelas, setTabelas] = useState<{ name: string; value: string }[]>([]);
  const [fields, errors, onChangeField, setForm, setErrors] = useFormFields<
    EmprestimoConsignadoCLTProps & {
      parcelas?: ConsigCLTParcelaProps[];
      prazo: number;
    }
  >();
  const { formControl, isReadOnly, dispatch } = useAttendanceContext();
  const { user } = useApplicationContext();
  const attendance = formControl.values as Attendance;
  const statusRef = useRef<POSTResponseSimulacaoCLT["status"] | null>(null);
  const finalizeConsultationRef = useRef(false);

  const selectedTableItem = fields.parcelas?.find(({ checked }) => checked);

  const isDisabledDigitar =
    !fields.banco ||
    isReadOnly ||
    !(
      statusRef.current === "FINISHED" &&
      (fields.banco === "TWO_S_CONSIG"
        ? fields.simulatedCodeTable && fields.prazo
        : fields.prazo && fields.prazo === selectedTableItem?.prazo)
    );

  const tabelaHasBeenSimulated =
    statusRef.current === "FINISHED" &&
    fields.simulatedCodeTable === selectedTableItem?.codigoTabela &&
    fields.prazo === selectedTableItem?.prazo;

  // &&
  //       fields.simulatedCodeTable === selectedTableItem?.codigoTabela &&
  //       fields.prazo === selectedTableItem?.prazo

  const POST = async (body = fields) => {
    return await apiDigitacao.post<POSTResponseSimulacaoCLT>(
      `/api/simulacoes/clt/atendimento/${attendance.id}/simular`,
      body
    );
  };
  const GET = async () => {
    return await apiDigitacao.get<GETResponseConsultaSimulacaoCLT>(
      `/api/simulacoes/clt/atendimento/${attendance.id}/consultar`
    );
  };

  const handleConsultar = async (
    response?: AxiosResponse<GETResponseConsultaSimulacaoCLT, any>,
    setLoading = setIsLoading
  ) => {
    if (!response) setLoading(true);
    try {
      const { data } = response || (await GET());
      const observacao = data.observacao;
      const simulacao = data.simulacao;
      const parcelas = data.parcelas;
      statusRef.current = data.status;
      const prazo = data.prazo;

      const simulatedCodeTable =
        data.status === "FINISHED" ? data.codTable : undefined;

      if (data.valorParcela && attendance.valorMargemClt == null && !response) {
        dispatch({
          type: "changeFieldWithInitial",
          payload: { fieldName: "valorMargemClt", data: data.valorParcela }
        });
      }

      setForm({
        ...simulacao,
        observacao,
        parcelas,
        prazo,
        simulatedCodeTable
      });
    } catch (e) {
    } finally {
      if (!response) setLoading(false);
    }
    if (statusRef.current === "PENDENT" && !response) {
      handleConsultationPulling(setLoading);
    }
  };

  const handleConsultationPulling = (
    setLoading = setIsLoading,
    onSimulated?: (
      res: AxiosResponse<GETResponseConsultaSimulacaoCLT, any>
    ) => void
  ) => {
    setLoading(true);
    requestLoop({
      promise: GET,
      actionByStep(response) {
        handleConsultar(response);
        if (finalizeConsultationRef.current) return { finalize: true };
        return {
          finalize: ["FINISHED", "SYSTEM_ERROR"].includes(response.data.status)
        };
      },
      onFinish(response) {
        setLoading(false);
        onSimulated?.(response);
      },
      onError() {
        return { finalize: true };
      },
      delay: 3000
    });
  };

  const handleSimular = async (
    setLoading = setIsLoading,
    onSimulated?: (
      res: AxiosResponse<GETResponseConsultaSimulacaoCLT, any>
    ) => void
  ) => {
    const { isValid, errors, body } = validateSimulacaoConsignadoCLT(
      fields,
      dispatch
    );

    if (!isValid) {
      console.log(errors);
      return setErrors(errors);
    }

    setLoading(true);
    try {
      const { data } = await POST(body);
      statusRef.current = data.status;
      const observacao = data.observacao;
      const parcelas = data.parcelas;
      const prazo = data.prazo;
      if (data.valorSalario && attendance.valorSalario == null) {
        dispatch({
          type: "changeFieldWithInitial",
          payload: { fieldName: "valorSalario", data: data.valorSalario }
        });
      }
      setForm({ ...data.simulacao, observacao, parcelas, prazo });
    } catch (e: any) {
      const errorsDetails = e?.response?.data?.details ?? {};
      if (Object.keys(errorsDetails).length) setErrors(errorsDetails);

      const errorMessage = e?.response?.data?.msgInterface;
      Toast({
        title: `Erro ao simular`,
        description: errorMessage,
        status: "error",
        duration: 20000
      });
    } finally {
      setLoading(false);
    }

    if (statusRef.current === "PENDENT") {
      handleConsultationPulling(setLoading, onSimulated);
    }
  };

  const attendanceDataToSimulacaoData = (newCpf: string | null) => {
    onChangeField("cpf", newCpf);
  };

  useEffect(() => {
    handleConsultar();
  }, []);

  useEffect(() => {
    finalizeConsultationRef.current = false;
    return () => {
      finalizeConsultationRef.current = true;
    };
  }, []);

  useEffect(() => {
    attendanceDataToSimulacaoData(attendance.cpf);
  }, [attendance.cpf]);

  useEffect(() => {
    if (fields.banco)
      getTabelaOptions({
        setDropdownTabela: setTabelas,
        setLoadingTabela: setIsLoadingTabela,
        bancoDigitacaoFGTSAPI: fields.banco,
        atendimento: attendance,
        user
      });
  }, [fields.banco]);

  return (
    <>
      <Grid
        templateColumns={telaAtendimento ? "1fr 1fr" : "1fr"}
        columnGap="20px"
        rowGap="40px"
        mb="15px"
      >
        <Box>
          <Box mb="15px">
            <DropdownField
              title="Banco"
              onChange={(value) => {
                onChangeField("banco", value);
              }}
              value={fields.banco}
              options={
                [1, 2787].includes(user.userData.customerId!) ||
                user.userData.parentCustomerId === 2787
                  ? bancosConsigCLTByCustomer1
                  : bancosConsigCLT
              }
              dropdownProps={{
                variant: "outline-custom",
                w: "full",
                loadingText: "Carregando",
                isDisabled: isReadOnly
              }}
              errorMessage={errors["banco"]}
            />
          </Box>

          {fields.banco !== "TWO_S_CONSIG" ? (
            <>
              <Text
                mb="8px"
                fontSize="15px"
                color="#4C545F"
                fontWeight="medium"
              >
                Tabelas
              </Text>
              <WithInvalidBox
                maxH="160px"
                w="100%"
                overflow="auto"
                borderRadius="10px"
                errorMessage={errors["codeTable"]}
              >
                {tabelas.length === 0 ? "Não há tabelas" : null}
                {tabelas.map(({ name, value }, i) => {
                  const isActive = fields.codeTable === value;
                  const isDisabled = isLoadingTabela || isReadOnly;
                  return (
                    <Flex
                      w="100%"
                      key={`${value}-${i}`}
                      border={"1px solid #E8EAED"}
                      alignItems="center"
                      px="16px"
                      py="4px"
                      minH="42px"
                      h="auto"
                      borderRadius="10px"
                      mb="10px"
                      cursor="pointer"
                      _hover={{ bg: "gray.50" }}
                      opacity={isDisabled ? 0.6 : 1}
                      pointerEvents={isDisabled ? "none" : undefined}
                      onClick={() => {
                        onChangeField("codeTable", value);
                      }}
                    >
                      <Center
                        borderRadius="full"
                        border="1px solid #141B3B"
                        w="12px"
                        minW="12px"
                        h="12px"
                        mr="9px"
                      >
                        <Box
                          bg={isActive ? "#141B3B" : ""}
                          borderRadius="full"
                          h="8px"
                          w="8px"
                        />
                      </Center>
                      <Text fontSize="15px" color="#4C545F">
                        {name}
                      </Text>
                    </Flex>
                  );
                })}
              </WithInvalidBox>
            </>
          ) : null}
        </Box>
        <Flex
          flexDir="column"
          justifyContent="center"
          bg="#F5F7F9"
          borderRadius="10px"
          p="15px 20px"
        >
          {fields.banco !== "TWO_S_CONSIG" ? (
            <>
              <Box mb="15px">
                <DropdownField
                  title="Tipo Simulação"
                  onChange={(value) => {
                    onChangeField("optionValue", value);
                  }}
                  value={fields.optionValue}
                  options={[
                    { name: "Selecione", value: undefined },
                    { name: "Valor liberado", value: 1 }
                  ]}
                  dropdownProps={{ variant: "outline-custom" }}
                  errorMessage={errors["optionValue"]}
                  isDisabled={fields.parcelas == null}
                />
              </Box>
              {fields.optionValue === 1 ? (
                <Box mb="15px">
                  <InputField
                    title="Valor liberado"
                    onChange={(e) =>
                      onChangeField(
                        "value",
                        currencyMaskFormat(e.target.value).raw
                      )
                    }
                    value={currencyMaskFormat(fields.value).value}
                    inputProps={{ variant: "outline-custom" }}
                    errorMessage={errors["value"]}
                  />
                </Box>
              ) : null}
              {fields.optionValue === 2 ? (
                <Box mb="15px">
                  <InputField
                    title="Valor parcela"
                    onChange={(e) =>
                      onChangeField(
                        "valueInstallment",
                        currencyMaskFormat(e.target.value).raw
                      )
                    }
                    value={currencyMaskFormat(fields.valueInstallment).value}
                    inputProps={{ variant: "outline-custom" }}
                    errorMessage={errors["valueInstallment"]}
                  />
                </Box>
              ) : null}
            </>
          ) : null}

          <Grid templateColumns="1fr 1fr" gap="10px">
            <Button
              w="100%"
              gridArea={`auto / span 2`}
              isLoading={isLoading}
              loadingText="Consultando"
              isDisabled={isLoadingTabela || isReadOnly}
              onClick={() => {
                onChangeField("parcelas", []);
                handleSimular();
              }}
            >
              Consultar
            </Button>
            <Button
              w="100%"
              gridArea={`auto / span 2`}
              loadingText="Validando Simulação"
              isDisabled={isDisabledDigitar}
              isLoading={isLoadingSimulacaoOnDigitacao}
              onClick={async () => {
                if (!tabelaHasBeenSimulated) {
                  handleSimular(
                    setIsLoadingSimulacaoOnDigitacao,
                    (response) => {
                      if (response.data.status !== "FINISHED") {
                        return Toast({
                          title: `Erro ao simular`,
                          description:
                            "Não foi possível validar a simulação para digitação. Tente novamente.",
                          status: "error",
                          duration: 10000
                        });
                      }
                      openModalDigitacaoConsigCLT({
                        bancoDigitacao: fields.banco!
                      });
                    }
                  );
                  return;
                }
                openModalDigitacaoConsigCLT({ bancoDigitacao: fields.banco! });
              }}
            >
              Digitar
            </Button>
          </Grid>
        </Flex>
        <Box gridArea={telaAtendimento ? "auto / span 2" : ""}>
          <AttendanceInput
            title="Observação Consulta:"
            value={fields.observacao}
            inputProps={{
              color:
                statusRef.current === "SYSTEM_ERROR"
                  ? "red"
                  : statusRef.current === "FINISHED"
                    ? "green.500"
                    : undefined,
              as: "textarea",
              height: "70px",
              minH: "60px",
              resize: "none"
            }}
          />
        </Box>
      </Grid>
      <CustomTableColor
        columns={getEmprestimoConsignadoCLTParcelasColumns({
          fields,
          onChangeField,
          isDisabledDigitar
        })}
        onClickRow={(row) =>
          setParcelasCLTConsig(
            { ...row, checked: !row.checked },
            onChangeField,
            fields
          )
        }
        rows={fields.parcelas ?? []}
        pageSize={11}
      />
    </>
  );
}

export const bancosConsigCLT = [{ name: "Facta", value: "FACTA" }];

const bancosConsigCLTByCustomer1 = [
  { name: "Facta", value: "FACTA" },
  { name: "2S Consig", value: "TWO_S_CONSIG" }
];
